import { ColumnsType } from 'antd/lib/table';
import { getTaskCulturesString } from 'app/containers/Task/slice/helpers';
import {
  CreateTaskWithRoutesPayload,
  IRouteWithSelectedImplement,
  ITasksArrResponseItem,
  RoutesTableItemType,
} from 'app/containers/Task/slice/types';
import i18next from 'i18next';
import RouteEmployeeSelect from '../RouteEmployeeSelect/RouteEmployeeSelect';
import RouteImplementSelect from '../RouteImplementSelect/RouteImplementSelect';
import moment from 'moment';
import { IEmployee, IImplement } from 'app/containers/Garage/types';
import { getAreaWithUnits } from 'utils/helpers';

export const getRouteTableColumns = () => {
  const columns: ColumnsType<RoutesTableItemType> = [
    {
      title: i18next?.t('Task.Tractor', 'Tractor'),
      dataIndex: 'model',
      key: 'model',
      render: (_: unknown, route) => route.model || route.gpsTransportId,
      sorter: false,
    },

    {
      title: i18next?.t('Common.Implement', 'Implement'),
      dataIndex: 'implement',
      key: 'implement',
      sorter: false,
      render: (_: unknown, route) => (
        <RouteImplementSelect routeId={route.id} />
      ),
    },

    {
      title: i18next?.t('Common.Employee', 'Employee'),
      dataIndex: 'employee',
      key: 'employee',
      sorter: false,
      render: (_: unknown, route) => <RouteEmployeeSelect routeId={route.id} />,
    },

    {
      title: i18next?.t('Task.Fields', 'Fields'),
      dataIndex: 'fields',
      key: 'fields',
      sorter: false,
      render: (_: unknown, route) => route.fieldSeasonIds.length,
    },

    {
      title: i18next?.t('Task.Area', 'Area'),
      dataIndex: 'area',
      key: 'area',
      render: (_: unknown, route) => route.area || '-',
      sorter: false,
    },
  ];

  return columns;
};

export const getMatchedTasksWithRouteTableColumns = () => {
  const columns: ColumnsType<ITasksArrResponseItem> = [
    {
      title: i18next?.t('Task.ListTable.Name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      render: (_: unknown, task) => task.name,
      sorter: false,
    },
    {
      title: i18next?.t('Common.Category', 'Category'),
      dataIndex: 'category',
      key: 'category',
      render: (_: unknown, task) => task.category.name,
      sorter: false,
    },

    {
      title: i18next?.t('Common.Dates', 'Dates'),
      dataIndex: 'dates',
      key: 'dates',
      sorter: false,
      render: (_: unknown, task) => {
        return getRouteDateRangeText(task.startDate, task.endDate);
      },
    },

    {
      title: i18next?.t('Common.Culture', 'Culture'),
      dataIndex: 'culture',
      key: 'culture',
      sorter: false,
      render: (_: unknown, task) => {
        const cultures = Array.from(
          new Set(
            task.fieldTasks?.map(
              fieldTask => fieldTask.fieldSeason.culture?.name,
            ),
          ),
        );

        const allCultures = getTaskCulturesString(cultures as any);
        return allCultures;
      },
    },

    {
      title: i18next?.t('Task.Area', 'Area'),
      dataIndex: 'area',
      key: 'area',
      sorter: false,
      render: (_: unknown, task) => getAreaWithUnits(task.totalArea * 10000),
    },

    {
      title: i18next?.t('Task.Done', 'Done'),
      dataIndex: 'done',
      key: 'done',
      sorter: false,
      render: (_: unknown, task) =>
        getAreaWithUnits(task.completedArea * 10000),
    },
  ];

  return columns;
};

export const IMPLEMENT_FORM_NAME_SEPARATOR = '___';
export const IMPLEMENT_FORM_NAME_IDENTIFIER = 'beaconId';

export const getImplementFormItemName = (routeId: number) =>
  `${routeId}${IMPLEMENT_FORM_NAME_SEPARATOR}${IMPLEMENT_FORM_NAME_IDENTIFIER}`;

export const EMPLOYEE_FORM_NAME_SEPARATOR = '___';
export const EMPLOYEE_FORM_NAME_IDENTIFIER = 'employeeId';

export const getEmployeeFormItemName = (routeId: number) =>
  `${routeId}${EMPLOYEE_FORM_NAME_SEPARATOR}${EMPLOYEE_FORM_NAME_IDENTIFIER}`;

export const getTaskCategoryIdsFromSelectedImplements = (
  selectedImplements?: IImplement[],
) => {
  return Array.from(
    new Set(
      selectedImplements?.reduce((accumulator: number[], implement) => {
        const taskCategoryIds = [
          implement.defaultTaskCategory.id,
          ...implement.taskCategories.map(taskCategory => taskCategory.id),
        ];

        accumulator.push(...taskCategoryIds);

        return accumulator;
      }, []),
    ),
  );
};

export const getRouteDateRangeText = (startDate: string, endDate: string) => {
  const start = moment(startDate);
  const end = moment(endDate);
  let result = start.format('MMMM D');

  const isSameMonth = start.isSame(end, 'month');
  if (!isSameMonth) {
    result = `${result} - ${end.format('MMMM D')}`;
  } else {
    const isSameDay = start.isSame(end, 'day');
    if (!isSameDay) {
      result = `${result} - ${end.format('D')}`;
    }
  }

  return result;
};

export const groupSameTransportWithImplementRoutes = (
  routes?: RoutesTableItemType[],
) => {
  const groupedRoutes =
    routes?.reduce((accumulator: RoutesTableItemType[], route) => {
      const similiarRouteIndex = accumulator.findIndex(
        accRoute =>
          accRoute.gpsTransportId === route.gpsTransportId &&
          accRoute.beaconId === route.beaconId,
      );

      if (similiarRouteIndex !== -1) {
        const accumulatorDistance = accumulator[similiarRouteIndex].distance;
        accumulator[similiarRouteIndex].routeIds.push(route.id);

        accumulator[similiarRouteIndex].distance =
          accumulatorDistance && route.distance
            ? accumulatorDistance + route.distance
            : route.distance;

        accumulator[similiarRouteIndex].fieldSeasonIds.push(
          ...route.fieldSeasonIds,
        );

        accumulator[similiarRouteIndex].fieldSeasonIds = Array.from(
          new Set(accumulator[similiarRouteIndex].fieldSeasonIds),
        );
      } else {
        accumulator.push({ ...route });
      }
      return accumulator;
    }, []) || [];

  return groupedRoutes;
};

export const mapGroupedRoutesWithSelectedImplement = ({
  groupedRoutes,
  rowsWithSelectedImplement,
  implementArray,
}: {
  groupedRoutes: RoutesTableItemType[];
  rowsWithSelectedImplement: IRouteWithSelectedImplement[];
  implementArray?: IImplement[];
}) => {
  return groupedRoutes.map(routeGroup => {
    const implementId = rowsWithSelectedImplement?.find(
      row => row.routeId === routeGroup.id,
    )?.implementId;

    const implementWidth =
      implementArray?.find(
        implement => implementId && implement.id === implementId,
      )?.width || 0;

    return {
      ...routeGroup,
      area:
        routeGroup.distance && implementWidth
          ? getAreaWithUnits(routeGroup.distance * implementWidth)
          : null,
    };
  });
};

export const getDefaultImplementIds = ({
  groupedRoutes,
  implementArray,
}: {
  groupedRoutes?: RoutesTableItemType[];
  implementArray?: IImplement[];
}) => {
  return (
    groupedRoutes?.map(route => {
      const implementId = implementArray?.find(
        implement =>
          route.beaconId && implement.gpsImplement?.beaconId === route.beaconId,
      )?.id;

      return {
        routeId: route.id,
        formItemName: getImplementFormItemName(route.id),
        implementId,
      };
    }) || []
  );
};

export const mapSelectedImplementsEmployeeAndRoouteToPayload = ({
  groupedRoutes,
  rowsWithSelectedImplement,
  implementArray,
  employeeArray,
  routeTableItems,
}: {
  groupedRoutes: RoutesTableItemType[];
  rowsWithSelectedImplement: IRouteWithSelectedImplement[];
  implementArray?: IImplement[];
  employeeArray?: IEmployee[];
  routeTableItems?: RoutesTableItemType[];
}) => {
  const routesData = groupedRoutes.reduce(
    (accumulator: CreateTaskWithRoutesPayload, routeGroup) => {
      const rowData = rowsWithSelectedImplement.find(
        row => row.routeId === routeGroup.id,
      );
      const implementId = rowData?.implementId;
      const employeeId = rowData?.employeeId;

      const implement = implementArray?.find(
        implementItem => implementItem.id === implementId,
      );

      const employee = employeeArray?.find(
        employeeItem => employeeItem.id === employeeId,
      );

      if (implement) {
        routeGroup.routeIds.forEach(id => {
          const route = routeTableItems?.find(route => route.id === id);
          const completedArea = route?.distance
            ? route.distance * implement.width
            : 0;
          accumulator.push({
            routeId: id,
            implementId: implement.id,
            beaconId: implement.gpsImplement?.beaconId,
            fieldId: route?.fieldSeasonIds[0] as number,
            completedArea,
            employeeId: employee?.id,
            slimId: employee?.gpsEmployee?.slimId,
          });
        });
      }
      return accumulator;
    },
    [],
  );

  return routesData;
};
